import { moneyAriaLabel } from '@beatq/shared';
import { ALERTS_RETENTION_MONTHS } from './catalog.js';
import type { AlertItem } from './invoicesReportsApi.js';
import { invoiceDetailPath, spendReportPath, exportCentrePath } from './invoicesReportsState.js';
import { approvalDetailPath } from '../policies-approvals/policiesApprovalsState.js';

/** Notification keys that are live approval *requests* (queue at 018), not facts. */
const APPROVAL_QUEUE_KEYS = new Set([
  'ba.approval_required.manager',
  'ba.approval_escalated.manager',
]);

const APPROVAL_QUEUE_KEY_FRAGMENTS = [
  'approval_required',
  'approval_escalated',
  'approval.requested',
  'approval-requested',
];

const QUEUE_KIND = /approval[._-]?request|pending[._-]?approval/i;

export function alertsInboxCutoff(now = new Date()): Date {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - ALERTS_RETENTION_MONTHS);
  return cutoff;
}

export function isWithinAlertsWindow(createdAt?: string | null, now = new Date()): boolean {
  if (!createdAt) return true;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return true;
  return t >= alertsInboxCutoff(now).getTime();
}

const APPROVAL_FACT = /(granted|approved|declined|rejected|expired|decided|outcome|policy breach)/i;

/**
 * 07-05 #52 / CHARTER: approval *decision requests* live at 018.
 * Keep approval *facts* (granted, declined, expired, policy breach).
 * Live GET /alerts often omits notificationKey — category APPROVAL without fact language is a queue item.
 */
export function isApprovalDecisionRequest(alert: AlertItem): boolean {
  const key = (alert.notificationKey ?? '').trim().toLowerCase();
  if (key && APPROVAL_QUEUE_KEYS.has(key)) return true;
  if (key && APPROVAL_QUEUE_KEY_FRAGMENTS.some((frag) => key.includes(frag))) return true;

  const kind = (alert.subjectKind ?? '').trim();
  if (QUEUE_KIND.test(kind)) return true;

  const semantic = (alert.semanticSubject ?? alert.semantic_subject ?? '').trim();
  if (QUEUE_KIND.test(semantic)) return true;

  const category = (alert.category ?? '').trim().toUpperCase();
  if (category === 'APPROVAL_REQUEST' || category === 'APPROVAL-REQUEST') return true;

  const haystack =
    `${alert.title ?? ''} ${alert.summary ?? ''} ${alert.body ?? ''} ${alert.category ?? ''}`.toLowerCase();
  const looksLikeQueueCopy =
    /(awaiting|pending|requires? approval|needs? (a )?decision|in the queue)/i.test(haystack);
  if (
    (category === 'APPROVAL' || category === 'APPROVALS' || kind.toLowerCase() === 'approval') &&
    looksLikeQueueCopy
  ) {
    return true;
  }
  if (
    (category === 'APPROVAL' || category === 'APPROVALS') &&
    !key &&
    !APPROVAL_FACT.test(haystack)
  ) {
    return true;
  }

  return false;
}

export function isEmployeeFacingFact(alert: AlertItem): boolean {
  const key = (alert.notificationKey ?? '').toLowerCase();
  if (key.includes('.employee')) return true;
  const audience = (alert.audience ?? '').toLowerCase();
  return audience === 'employee' || audience === 'be';
}

export function deriveSemanticSubject(alert: AlertItem): string | null {
  const kind = alert.subjectKind?.trim() || null;
  const id = alert.subjectId?.trim() || null;
  if (kind && id) return `${kind}:${id}`;
  if (alert.semanticSubject?.trim()) return alert.semanticSubject.trim();
  if (alert.semantic_subject?.trim()) return alert.semantic_subject.trim();
  if (id) return id;
  return null;
}

export function alertSubjectKey(alert: AlertItem): string {
  const semantic = deriveSemanticSubject(alert);
  if (semantic) return semantic;
  const category = alert.category?.trim();
  if (category) return category;
  return 'Other';
}

export function humanizeAlertSubject(key: string): string {
  if (key.startsWith('INVOICE:') || key.startsWith('invoice:'))
    return `Invoice ${key.slice(key.indexOf(':') + 1)}`;
  if (key.startsWith('BUDGET:') || key.startsWith('budget:'))
    return `Budget ${key.slice(key.indexOf(':') + 1)}`;
  if (key.startsWith('ORDER:') || key.startsWith('order:'))
    return `Order ${key.slice(key.indexOf(':') + 1)}`;
  return key.replace(/[_:]+/g, ' ').trim();
}

export function filterAlertsInbox(
  alerts: AlertItem[],
  opts: {
    department?: string | null;
    departmentId?: string | null;
    category?: string | null;
    unreadOnly?: boolean;
    now?: Date;
  } = {},
): AlertItem[] {
  return alerts.filter((alert) => {
    if (isApprovalDecisionRequest(alert)) return false;
    if (!isWithinAlertsWindow(alert.createdAt, opts.now)) return false;
    if (opts.unreadOnly && isAlertRead(alert)) return false;
    if (opts.departmentId) {
      if ((alert.departmentId ?? '').trim() !== opts.departmentId) return false;
    } else if (opts.department) {
      const dept = (alert.departmentName ?? alert.departmentId ?? '').trim();
      if (dept !== opts.department) return false;
    }
    if (opts.category) {
      const cat = (alert.category ?? '').trim();
      if (cat !== opts.category) return false;
    }
    return true;
  });
}

export function groupAlertsBySubject(alerts: AlertItem[]): Map<string, AlertItem[]> {
  const groups = new Map<string, AlertItem[]>();
  const sorted = [...alerts].sort((a, b) => {
    const ta = Date.parse(a.createdAt ?? '') || 0;
    const tb = Date.parse(b.createdAt ?? '') || 0;
    return tb - ta;
  });
  for (const alert of sorted) {
    const key = alertSubjectKey(alert);
    const bucket = groups.get(key) ?? [];
    bucket.push(alert);
    groups.set(key, bucket);
  }
  return groups;
}

export function isAlertRead(alert: AlertItem): boolean {
  if (alert.isRead === true) return true;
  return (alert.status ?? '').toUpperCase() === 'READ';
}

export function departmentOptions(alerts: AlertItem[]): string[] {
  const set = new Set<string>();
  for (const alert of alerts) {
    const name = alert.departmentName?.trim() || alert.departmentId?.trim();
    if (name) set.add(name);
  }
  return [...set].sort();
}

export function enrichAlertsWithDepartments(
  alerts: AlertItem[],
  departments: Array<{ id?: string; name?: string | null; code?: string | null }>,
): AlertItem[] {
  const names = new Map<string, string>();
  for (const dept of departments) {
    if (!dept.id) continue;
    names.set(dept.id, dept.name?.trim() || dept.code?.trim() || dept.id);
  }
  return alerts.map((alert) => {
    if (alert.departmentName?.trim()) return alert;
    const fromId = alert.departmentId ? names.get(alert.departmentId) : undefined;
    return fromId ? { ...alert, departmentName: fromId } : alert;
  });
}

export function speakMoneyInText(text: string): string {
  return text.replace(/£\s*([\d,]+)(?:\.(\d{2}))?/g, (_match, pounds: string, pence?: string) => {
    const minor = Number(pounds.replace(/,/g, '')) * 100 + Number(pence ?? '0');
    if (!Number.isFinite(minor)) return _match;
    return moneyAriaLabel(minor);
  });
}

export function alertAccessibleName(alert: AlertItem): string {
  const body = !isEmployeeFacingFact(alert) ? alert.body : null;
  const spokenBody = body ? speakMoneyInText(body) : null;
  const amount =
    alert.amountMinor != null && Number.isFinite(alert.amountMinor)
      ? moneyAriaLabel(alert.amountMinor)
      : null;
  const parts = [
    alert.title ?? alert.summary ?? 'Notification',
    alert.departmentName,
    alert.employeeName,
    amount,
    spokenBody,
  ].filter((part): part is string => Boolean(part && String(part).trim()));
  return parts.join(', ');
}

export function categoryOptions(alerts: AlertItem[]): string[] {
  const set = new Set<string>();
  for (const alert of alerts) {
    const cat = alert.category?.trim();
    if (cat) set.add(cat);
  }
  return [...set].sort();
}

export function alertOpenHrefs(alert: AlertItem): { open: string | null; subject: string | null } {
  const subject = alertSubjectPath(alert);
  const deep = alert.deepLink?.startsWith('/') ? alert.deepLink : null;
  if (deep && subject && deep !== subject) return { open: deep, subject };
  return { open: subject ?? deep, subject: null };
}

export function alertSubjectPath(alert: AlertItem): string | null {
  const kind = (alert.subjectKind ?? '').toLowerCase();
  const id = alert.subjectId?.trim();
  if ((kind.includes('invoice') || kind === 'invoicing') && id) return invoiceDetailPath(id);
  if (kind.includes('approval') && id) return approvalDetailPath(id);
  if (kind.includes('budget') || kind.includes('spend')) return spendReportPath();
  if (kind.includes('export')) return exportCentrePath();
  const semantic = (alert.semanticSubject ?? '').toLowerCase();
  const invoiceFromSemantic = semantic.match(/invoice[:/]([a-z0-9-]+)/i);
  if (invoiceFromSemantic?.[1]) return invoiceDetailPath(invoiceFromSemantic[1]);
  if (alert.deepLink?.startsWith('/')) return alert.deepLink;
  return null;
}
